import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

import { Textarea } from "../components/ui/textarea";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { 
  User, 
  FileText, 
  Upload, 
  CheckCircle, 
  ArrowRight, 
  ArrowLeft,
  X,
  FileIcon,
  CreditCard,
  Building,
  ClipboardList,
  Plus,
  Trash2
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const steps = [
  { id: 1, title: "Contact Info", icon: User },
  { id: 2, title: "Tax Details", icon: FileText },
  { id: 3, title: "Direct Deposit", icon: Building },
  { id: 4, title: "Documents", icon: Upload },
  { id: 5, title: "Eligibility", icon: ClipboardList },
  { id: 6, title: "Review & Submit", icon: CheckCircle }
];

const serviceTypes = [
  { id: "individual_tax_prep", label: "Individual Tax Preparation" },
  { id: "self_employed", label: "Self-Employed & 1099 Tax Services" },
  { id: "business_tax_prep", label: "Business Tax Preparation" },
  { id: "credit_repair", label: "Credit Repair" }
];

const incomeTypes = [
  { id: "w2", label: "W-2" },
  { id: "1099", label: "1099" },
  { id: "both", label: "Both" },
  { id: "any_wages", label: "Any form of wages" }
];

const filingStatuses = [
  { value: "single", label: "Single" },
  { value: "head_of_household", label: "Head of Household" },
  { value: "married_filing_jointly", label: "Married Filing Jointly" },
  { value: "married_filing_separately", label: "Married Filing Separately" },
  { value: "surviving_spouse", label: "Surviving Spouse" }
];

const maritalStatuses = [
  { value: "married", label: "Married" },
  { value: "single", label: "Single" },
  { value: "divorced", label: "Divorced" },
  { value: "legally_separated", label: "Legally separated" },
  { value: "considered_unmarried", label: "Considered unmarried" }
];

const DocumentUploadGroup = ({
  title,
  required,
  fileType,
  error,
  accept = "image/*,.pdf",
  uploadedFiles,
  uploadProgress,
  addDocumentSlot,
  removeDocumentSlot,
  handleMultiFileUpload,
}) => {
  const rows = uploadedFiles[fileType] || [{ file: null, fileId: null, progress: null }];

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <Label className="text-navy font-medium">
          {title}{required ? " *" : ""}
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => addDocumentSlot(fileType)}
          className="h-8 w-10 px-0"
          data-testid={`add-${fileType}`}
          title="Add another document"
        >
          +
        </Button>
      </div>

      <div className={`mt-2 space-y-3 ${error ? "border border-red-500 rounded-lg p-2" : ""}`}>
        {rows.map((row, index) => {
          const progressKey = `${fileType}_${index}`;
          const pct = uploadProgress[progressKey];

          return (
            <div key={progressKey} className="rounded-lg border border-slate-200 bg-white">
              <div className="flex items-center justify-between gap-3 p-3">
                <div className="flex items-center gap-2 min-w-0">
                  {row?.file ? (
                    <>
                      <FileIcon size={20} className="text-green-600" />
                      <span className="text-sm text-green-800 break-all">{row.file.name}</span>
                    </>
                  ) : (
                    <span className="text-sm text-slate-500">No file uploaded</span>
                  )}
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  <label className="inline-flex items-center justify-center">
                    <span className="rounded-md bg-navy text-white px-3 py-2 text-xs cursor-pointer hover:bg-navy-light">
                      Upload/Take Pictures
                    </span>
                    <input
                      type="file"
                      className="hidden"
                      accept={accept}
                      onChange={(e) => handleMultiFileUpload(fileType, index, e)}
                    />
                  </label>

                  {rows.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeDocumentSlot(fileType, index)}
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                      title="Remove"
                    >
                      <X size={16} />
                    </Button>
                  )}
                </div>
              </div>

              {pct !== undefined && (
                <div className="px-3 pb-3">
                  <div className="h-2 w-full rounded bg-slate-200 overflow-hidden">
                    <div className="h-2 bg-navy" style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Uploading… {pct}%</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const IntakeForm = () => {

  // Convert UI MM/DD/YYYY to backend-friendly YYYY-MM-DD
  const mmddyyyyToIso = (value) => {
    if (!value) return "";
    const parts = value.split("/").map((p) => p.trim());
    if (parts.length !== 3) return "";
    const [mm, dd, yyyy] = parts;
    if (!mm || !dd || !yyyy) return "";
    if (yyyy.length !== 4) return "";
    return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  };

  const navigate = useNavigate();
  const [uploadProgress, setUploadProgress] = useState({});
  const [showPortalSignupPrompt, setShowPortalSignupPrompt] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState({
    state_id: [{ file: null, fileId: null, progress: null }],
    ssn_card: [{ file: null, fileId: null, progress: null }],
    dependent_ssn: [{ file: null, fileId: null, progress: null }],
    dependent_birth_cert: [{ file: null, fileId: null, progress: null }],
    w2_1099: [{ file: null, fileId: null, progress: null }],
    deduction_receipts: [{ file: null, fileId: null, progress: null }],
    school_form: [{ file: null, fileId: null, progress: null }],
    health_insurance: [{ file: null, fileId: null, progress: null }]
  });

  const [otherDocs, setOtherDocs] = useState([{ name: "", file: null, fileId: null, progress: null }]);

  const [documentErrors, setDocumentErrors] = useState({
    state_id: false,
    ssn_card: false,
    w2_1099: false,
  });

  const [uploadedFileIds, setUploadedFileIds] = useState({
    state_id: [],
    ssn_card: [],
    dependent_ssn: [],
    dependent_birth_cert: [],
    w2_1099: [],
    deduction_receipts: [],
    school_form: [],
    health_insurance: []
  });

  const formatDateInput = (value) => {
    // Simple MM/DD/YYYY formatter: keeps digits and inserts slashes,
    // while allowing partial entry (e.g., "0" -> "0", "01" -> "01", "010" -> "01/0").
    const digits = String(value || "").replace(/[^0-9]/g, "").slice(0, 8);
    if (!digits) return "";

    const mm = digits.slice(0, 2);
    const dd = digits.slice(2, 4);
    const yyyy = digits.slice(4, 8);

    let out = mm;
    if (digits.length > 2) out += "/" + dd;
    if (digits.length > 4) out += "/" + yyyy;
    return out;
  };

  
  const compressImageIfNeeded = async (file) => {
    try {
      if (!file || !file.type?.startsWith('image/')) return file;
      // Only compress larger images to reduce upload time
      if (file.size < 1_500_000) return file;

      const img = new Image();
      const url = URL.createObjectURL(file);
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = url;
      });

      const maxDim = 1600;
      let { width, height } = img;
      if (width > height && width > maxDim) {
        height = Math.round((height * maxDim) / width);
        width = maxDim;
      } else if (height >= width && height > maxDim) {
        width = Math.round((width * maxDim) / height);
        height = maxDim;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.8));
      URL.revokeObjectURL(url);
      if (!blob) return file;

      return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
    } catch {
      return file;
    }
  };

  const [formData, setFormData] = useState({
    contact: {
      first_name: "",
      last_name: "",
      date_of_birth: "",
      gender: "",
      status_type: "",
      status_type_other: "",
      phone: "",
      phone_carrier: "",
      phone_carrier_other: "",
      ssn: "",
      email: "",
      address: "",
      city: "",
      state: "",
      zip_code: "",
      referral_name: ""
    },
    tax_details: {
      filing_status: "",
      spouse_first_name: "",
      spouse_last_name: "",
      spouse_ssn: "",
      spouse_dob: "",
      spouse_occupation: "",
      spouse_status_type: "",
      spouse_phone: "",
      spouse_phone_carrier: "",
      spouse_phone_carrier_other: "",
      spouse_email: "",
      spouse_gender: "",
      occupation: "",
      services_needed: [],
      income_type: [],
      dependents: []
    },
    direct_deposit: {
      bank_name: "",
      routing_number: "",
      account_number: "",
      account_type: ""
    },
    eligibility: {
      marital_status_dec31: "",
      dependent_lived_with_you: null,
      paid_more_than_half_expenses: null,
      dependent_is_qualifying_relative: null,
      anyone_else_can_claim_dependent: null,
      enrolled_half_time_school: null,
      completed_less_than_4_years_college: null,
      paid_education_expenses: null
    },
    confirmations: {
      info_is_accurate: false,
      understand_one_person_claim: false,
      all_income_disclosed: false,
      understand_penalties: false,
      consent_sms: false
    },
    documents: {
      additional_notes: ""
    },
    signature: ""
  });

  // Handle adding a dependent
  const addDependent = () => {
    setFormData(prev => ({
      ...prev,
      tax_details: {
        ...prev.tax_details,
        dependents: [
          ...prev.tax_details.dependents,
          { first_name: "", middle_name: "", last_name: "", relationship: "", gender: "", dob: "", ssn: "" },
        ],
      },
    }));
  };

  // Handle removing a dependent
  const removeDependent = (index) => {
    setFormData(prev => ({
      ...prev,
      tax_details: {
        ...prev.tax_details,
        dependents: prev.tax_details.dependents.filter((_, i) => i !== index)
      }
    }));
  };

  // Handle dependent field change
  const updateDependent = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      tax_details: {
        ...prev.tax_details,
        dependents: prev.tax_details.dependents.map((dep, i) => 
          i === index ? { ...dep, [field]: value } : dep
        )
      }
    }));
  };


  const updateContact = (field, value) => {
    setFormData(prev => ({
      ...prev,
      contact: { ...prev.contact, [field]: value }
    }));
  };

  const appointmentSlots = [
    "10:00 AM",
    "10:40 AM",
    "11:20 AM",
    "12:00 PM",
    "12:40 PM",
    "1:20 PM",
    "2:00 PM",
    "2:40 PM",
    "3:20 PM",
    "4:00 PM",
    "4:40 PM",
    "5:20 PM",
  ];

  const time12To24 = (t) => {
    // e.g. "1:20 PM" -> "13:20", "10:00 AM" -> "10:00"
    if (!t) return "";
    const m = String(t).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!m) return String(t).trim();
    let hour = parseInt(m[1], 10);
    const minute = m[2];
    const ampm = m[3].toUpperCase();
    if (ampm === "AM") {
      if (hour === 12) hour = 0;
    } else {
      if (hour !== 12) hour += 12;
    }
    return `${String(hour).padStart(2, "0")}:${minute}`;
  };

  const updateTaxDetails = (field, value) => {
    setFormData(prev => ({
      ...prev,
      tax_details: { ...prev.tax_details, [field]: value }
    }));
  };

  const updateDirectDeposit = (field, value) => {
    setFormData(prev => ({
      ...prev,
      direct_deposit: { ...prev.direct_deposit, [field]: value }
    }));
  };

  const updateEligibility = (field, value) => {
    setFormData(prev => ({
      ...prev,
      eligibility: { ...prev.eligibility, [field]: value }
    }));
  };

  const updateConfirmations = (field, value) => {
    setFormData(prev => ({
      ...prev,
      confirmations: { ...prev.confirmations, [field]: value }
    }));
  };

  const toggleService = (serviceId) => {
    setFormData(prev => {
      const current = prev.tax_details.services_needed;
      const updated = current.includes(serviceId)
        ? current.filter(id => id !== serviceId)
        : [...current, serviceId];
      return {
        ...prev,
        tax_details: { ...prev.tax_details, services_needed: updated }
      };
    });
  };

  const toggleIncomeType = (incomeId) => {
    setFormData(prev => {
      const current = prev.tax_details.income_type;
      const updated = current.includes(incomeId)
        ? current.filter(id => id !== incomeId)
        : [...current, incomeId];
      return {
        ...prev,
        tax_details: { ...prev.tax_details, income_type: updated }
      };
    });
  };

  const addDocumentSlot = (fileType) => {
    setUploadedFiles((prev) => ({
      ...prev,
      [fileType]: [...(prev[fileType] || []), { file: null, fileId: null, progress: null }],
    }));
  };

  const removeDocumentSlot = (fileType, index) => {
    setUploadedFiles((prev) => {
      const nextArr = (prev[fileType] || []).filter((_, i) => i !== index);
      return { ...prev, [fileType]: nextArr.length ? nextArr : [{ file: null, fileId: null, progress: null }] };
    });

    setUploadedFileIds((prev) => {
      const nextIds = (prev[fileType] || []).filter((_, i) => i !== index);
      return { ...prev, [fileType]: nextIds };
    });

    setUploadProgress((prev) => {
      const next = { ...prev };
      if (next[`${fileType}_${index}`] !== undefined) delete next[`${fileType}_${index}`];
      return next;
    });
  };

  const handleMultiFileUpload = useCallback(async (fileType, index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadProgress((prev) => ({ ...prev, [`${fileType}_${index}`]: 1 }));
    const fileToUpload = await compressImageIfNeeded(file);

    const start = Date.now();

    if (fileToUpload.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      return;
    }

    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", fileToUpload);

      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          try {
            const total = progressEvent.total || fileToUpload.size;
            const pct = Math.round((progressEvent.loaded * 100) / total);
            setUploadProgress((prev) => ({ ...prev, [`${fileType}_${index}`]: Math.min(100, Math.max(0, pct)) }));
          } catch {
            // ignore
          }
        },
      });

      setUploadedFiles((prev) => ({
        ...prev,
        [fileType]: (prev[fileType] || []).map((row, i) => (i === index ? { ...row, file: fileToUpload, fileId: response.data.id, progress: 100 } : row)),
      }));

      setUploadedFileIds((prev) => ({
        ...prev,
        [fileType]: (prev[fileType] || []).map((idRow, i) => (i === index ? response.data.id : idRow)),
      }));

      const elapsed = Math.round((Date.now() - start) / 1000);
      toast.success(`${fileToUpload.name} uploaded successfully${elapsed ? ` (${elapsed}s)` : ""}`);
    } catch (err) {
      console.error("Upload error:", err);
      setUploadProgress((prev) => {
        const next = { ...prev };
        delete next[`${fileType}_${index}`];
        return next;
      });
      toast.error("Failed to upload file. Please try again.");
    }
  }, []);


  const handleOtherDocUpload = async (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setOtherDocs((prev) => prev.map((d, i) => (i === index ? { ...d, progress: 1, file } : d)));

    const fileToUpload = await compressImageIfNeeded(file);
    if (fileToUpload.size > 10 * 1024 * 1024) {
      toast.error("File must be less than 10MB");
      setOtherDocs((prev) => prev.map((d, i) => (i === index ? { ...d, progress: null } : d)));
      return;
    }

    try {
      const start = Date.now();
      const formDataUpload = new FormData();
      formDataUpload.append("file", fileToUpload);

      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          try {
            const total = progressEvent.total || fileToUpload.size;
            const pct = Math.round((progressEvent.loaded * 100) / total);
            setOtherDocs((prev) => prev.map((d, i) => (i === index ? { ...d, progress: pct } : d)));
          } catch {
            // ignore
          }
        },
      });

      const elapsed = Math.round((Date.now() - start) / 1000);
      setOtherDocs((prev) =>
        prev.map((d, i) => (i === index ? { ...d, file: fileToUpload, fileId: response.data.id, progress: 100 } : d))
      );
      toast.success(`${fileToUpload.name} uploaded successfully${elapsed ? ` (${elapsed}s)` : ""}`);
    } catch (err) {
      console.error("Upload error:", err);
      setOtherDocs((prev) => prev.map((d, i) => (i === index ? { ...d, progress: null } : d)));
      toast.error("Failed to upload file. Please try again.");
    }
  };

  const handleFileUpload = useCallback(async (fileType, e) => {
    // Legacy calls route into the multi-upload handler at index 0
    return handleMultiFileUpload(fileType, 0, e);
  }, [handleMultiFileUpload]);

  const removeFile = (fileType) => {
    // legacy single-file remover (kept for compatibility)
    removeDocumentSlot(fileType, 0);
  };

  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentTime, setAppointmentTime] = useState("");

  const [appointmentPopoverOpen, setAppointmentPopoverOpen] = useState(false);
  const [intakeConsent, setIntakeConsent] = useState(false);
  const [intakeConsent2, setIntakeConsent2] = useState(false);

  const validateStep = () => {
    // Step 1: Contact info
    if (currentStep === 1) {
      const c = formData.contact;
      const missingFields = [];
      if (!c.first_name) missingFields.push('First Name');
      if (!c.last_name) missingFields.push('Last Name');
      if (!c.date_of_birth) missingFields.push('Date of Birth');
      if (!c.gender) missingFields.push('Gender');
      if (!c.status_type) missingFields.push('Status Type');
      if (c.status_type === 'other' && !c.status_type_other) missingFields.push('Status Type (Other)');
      if (!c.ssn) missingFields.push('SSN');
      if (!c.phone) missingFields.push('Phone');
      if (!c.email) missingFields.push('Email');
      if (!c.address) missingFields.push('Address');
      if (!c.city) missingFields.push('City');
      if (!c.state) missingFields.push('State');
      if (!c.zip_code) missingFields.push('ZIP Code');

      if (missingFields.length > 0) {
        toast.error(`Please complete: ${missingFields.join(', ')}`);
        return false;
      }

      // Validate DOB format MM/DD/YYYY
      if (c.date_of_birth && c.date_of_birth.length !== 10) {
        toast.error("Date of Birth must be in MM/DD/YYYY format.");
        return false;
      }
    }

    // Step 2: Tax details
    if (currentStep === 2) {
      const t = formData.tax_details;
      if (!t.filing_status) {
      // Other documents must be named + uploaded if row is used
      const anyOtherUsed = otherDocs.some((d) => d.name.trim() || d.fileId);
      if (anyOtherUsed) {
        for (const d of otherDocs) {
          if (!d.name.trim()) {
            toast.error("Please name each Other Document.");
            return false;
          }
          if (!d.fileId) {
            toast.error("Please upload each Other Document you added.");
            return false;
          }
        }
      }

        toast.error("Please select your filing status.");
        return false;
      }
      if (!t.income_type || t.income_type.length === 0) {
        toast.error("Please select at least one income type.");
        return false;
      }
      if (!t.occupation) {
        toast.error("Please enter your occupation.");
        return false;
      }
      if (!t.services_needed || t.services_needed.length === 0) {
        toast.error("Please select at least one financial service.");
        return false;
      }
      if (t.filing_status === "married_filing_jointly") {

      // Validate dependent DOBs
      const deps = t.dependents || [];
      if (Array.isArray(deps)) {
        for (const dep of deps) {
          if (dep?.dob && dep.dob.length !== 10) {
            toast.error("Each dependent DOB must be in MM/DD/YYYY format.");
            return false;
          }
        }
      }

        if (!t.spouse_first_name || !t.spouse_last_name || !t.spouse_ssn || !t.spouse_dob) {
          toast.error("Please complete all spouse information fields.");
          return false;
        }
      }
    }

    // Step 3: Direct deposit
    if (currentStep === 3) {
      const d = formData.direct_deposit;
      if (!d.bank_name || !d.routing_number || !d.account_number || !d.account_type) {
        toast.error("Please complete all direct deposit fields.");
        return false;
      }
    }

    // Step 4: Documents
    if (currentStep === 4) {
      const hasStateId = (uploadedFiles.state_id || []).some((r) => r?.fileId);
      const hasSsn = (uploadedFiles.ssn_card || []).some((r) => r?.fileId);
      const hasW2 = (uploadedFiles.w2_1099 || []).some((r) => r?.fileId);

      setDocumentErrors({
        state_id: !hasStateId,
        ssn_card: !hasSsn,
        w2_1099: !hasW2,
      });

      if (!hasStateId || !hasSsn || !hasW2) {
        toast.error('Please upload all required documents');
        return false;
      }
    }

    // Step 5: Eligibility questionnaire
    if (currentStep === 5) {
      // Validate appointment preferred date format if provided
      if (appointmentDate && appointmentDate.length !== 10) {
        toast.error("Preferred Date must be in MM/DD/YYYY format.");
        return;
      }

      const e = formData.eligibility;
      if (!e.marital_status_dec31) {
        toast.error("Please select your marital status as of December 31.");
        return false;
      }
      const boolFields = [
        "dependent_lived_with_you",
        "paid_more_than_half_expenses",
        "dependent_is_qualifying_relative",
        "anyone_else_can_claim_dependent",
      ];
      const hasNull = boolFields.some((field) => e[field] === null || e[field] === undefined);
      if (hasNull) {
        toast.error("Please answer all eligibility questions before continuing.");
        return false;
      }
    }

    return true;
  };

  const nextStep = () => {
    if (!validateStep()) return;

    if (currentStep < steps.length) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    // Validate confirmations - only require the two consent checkboxes
    if (!intakeConsent) {
      toast.error("Please confirm the consent checkbox");
      return;
    }

    if (!formData.signature.trim()) {
      toast.error("Please provide your signature");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Prepare submission data
      const submissionData = {
        contact: {
          ...formData.contact,
          date_of_birth: mmddyyyyToIso(formData.contact.date_of_birth),
        },
        appointment: appointmentDate && appointmentTime ? { date: mmddyyyyToIso(appointmentDate), start_time: time12To24(appointmentTime) } : null,
        tax_details: {
          ...formData.tax_details,
          dependents: formData.tax_details.dependents.map(dep => ({
            name: [dep.first_name, dep.middle_name, dep.last_name].filter(Boolean).join(" "),
            relationship: dep.relationship,
            gender: dep.gender,
            dob: mmddyyyyToIso(dep.dob),
            ssn: dep.ssn
          }))
        },
        documents: {
          document_names: [
            ...Object.entries(uploadedFiles).flatMap(([type, rows]) =>
              (rows || [])
                .filter((r) => r?.file)
                .map((r) => `${type}: ${r.file.name}`)
            ),
            ...otherDocs
              .filter((d) => d.file)
              .map((d) => `other:${d.name.trim()}: ${d.file.name}`),
          ],
          document_ids: [
            ...Object.entries(uploadedFileIds).flatMap(([type, ids]) =>
              (ids || [])
                .filter(Boolean)
                .map((id) => `${type}: ${id}`)
            ),
            ...otherDocs
              .filter((d) => d.fileId)
              .map((d) => `other:${d.name.trim()}: ${d.fileId}`),
          ],
          additional_notes: formData.documents.additional_notes,
          direct_deposit: formData.direct_deposit,
          eligibility: formData.eligibility,
          confirmations: formData.confirmations,
          signature: formData.signature
        }
      };

      const response = await axios.post(`${API}/intake`, submissionData);
      const createdIntake = response.data;



      toast.success("Your intake form has been submitted successfully!");
      
      // Store the email and show portal signup prompt
      setSubmittedEmail(formData.contact.email);
      setShowPortalSignupPrompt(true);
    } catch (error) {
      console.error("Submission error:", error);
      
      // Handle duplicate submission error (409 Conflict)
      if (error.response?.status === 409) {
        toast.error(error.response.data.detail || "A submission already exists. Please log in to your Client Portal to update your information.", {
          duration: 8000
        });
      } else {
        toast.error("Failed to submit form. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const progress = (currentStep / steps.length) * 100;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="pt-[100px] md:pt-[110px] pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between mb-2">
              {steps.map((step) => (
                <div
                  key={step.id}
                  className={`flex flex-col items-center ${
                    step.id <= currentStep ? "text-navy" : "text-slate-400"
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors duration-300 ${
                      step.id < currentStep
                        ? "bg-gold text-navy"
                        : step.id === currentStep
                        ? "bg-navy text-white"
                        : "bg-slate-200 text-slate-500"
                    }`}
                  >
                    {step.id < currentStep ? (
                      <CheckCircle size={16} />
                    ) : (
                      step.id
                    )}
                  </div>
                  <span className="text-xs mt-1 hidden sm:block">{step.title}</span>
                </div>
              ))}
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Form Card */}
          <Card className="border-0 shadow-card">
            <CardHeader className="border-b border-slate-100">
              <CardTitle className="font-heading text-xl text-navy flex items-center gap-3">
                {(() => {
                  const StepIcon = steps[currentStep - 1].icon;
                  return <StepIcon className="text-gold" size={24} />;
                })()}
                {steps[currentStep - 1].title}
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Step 1: Contact Info */}
                  {currentStep === 1 && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="first_name" className="text-navy font-medium">First Name *</Label>
                          <Input
                            id="first_name"
                            data-testid="input-first-name"
                            value={formData.contact.first_name}
                            onChange={(e) => updateContact('first_name', e.target.value)}
                            className="mt-2 h-12"
                            placeholder="John"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="last_name" className="text-navy font-medium">Last Name *</Label>
                          <Input
                            id="last_name"
                            data-testid="input-last-name"
                            value={formData.contact.last_name}
                            onChange={(e) => updateContact('last_name', e.target.value)}
                            className="mt-2 h-12"
                            placeholder="Doe"
                            required
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="dob" className="text-navy font-medium">Date of Birth *</Label>
                          <Input
                            id="dob"
                            type="text"
                            inputMode="numeric"
                            data-testid="input-dob"
                            value={formData.contact.date_of_birth}
                            onChange={(e) => updateContact('date_of_birth', formatDateInput(e.target.value))}
                            className="mt-2 h-12"
                            placeholder="MM/DD/YYYY"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="ssn" className="text-navy font-medium">Social Security Number *</Label>
                          <Input
                            id="ssn"
                            data-testid="input-ssn"
                            value={formData.contact.ssn}
                            onChange={(e) => updateContact('ssn', e.target.value)}
                            className="mt-2 h-12"
                            placeholder="XXX-XX-XXXX"
                            required
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-navy font-medium">Gender *</Label>
                          <Select
                            value={formData.contact.gender}
                            onValueChange={(value) => updateContact('gender', value)}
                          >
                            <SelectTrigger className="mt-2 h-12" data-testid="select-client-gender">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Male">Male</SelectItem>
                              <SelectItem value="Female">Female</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label className="text-navy font-medium">Status Type *</Label>
                          <Select
                            value={formData.contact.status_type}
                            onValueChange={(value) => updateContact('status_type', value)}
                          >
                            <SelectTrigger className="mt-2 h-12" data-testid="select-status-type">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="us_citizen_born">U.S. Citizen (born in the U.S.)</SelectItem>
                              <SelectItem value="us_citizen_naturalized">U.S. Citizen (naturalized)</SelectItem>
                              <SelectItem value="lpr">Lawful Permanent Resident (Green Card holder)</SelectItem>
                              <SelectItem value="resident_alien">Resident Alien</SelectItem>
                              <SelectItem value="nonresident_alien">Nonresident Alien</SelectItem>
                              <SelectItem value="itin_holder">ITIN Holder (no Social Security Number)</SelectItem>
                              <SelectItem value="other">Other (please specify)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {formData.contact.status_type === 'other' && (
                        <div>
                          <Label htmlFor="status_type_other" className="text-navy font-medium">Other (please specify)</Label>
                          <Input
                            id="status_type_other"
                            data-testid="input-status-type-other"
                            value={formData.contact.status_type_other}
                            onChange={(e) => updateContact('status_type_other', e.target.value)}
                            className="mt-2 h-12"
                            placeholder="Please specify"
                          />
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="phone" className="text-navy font-medium">Phone Number *</Label>
                          <Input
                            id="phone"
                            type="tel"
                            data-testid="input-phone"
                            value={formData.contact.phone}
                            onChange={(e) => updateContact('phone', e.target.value)}
                            className="mt-2 h-12"
                            placeholder="(555) 123-4567"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="phone_carrier" className="text-navy font-medium">Phone Carrier/Company</Label>
                          <select
                            id="phone_carrier"
                            data-testid="input-phone-carrier"
                            value={formData.contact.phone_carrier}
                            onChange={(e) => updateContact('phone_carrier', e.target.value)}
                            className="mt-2 h-12 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-navy cursor-pointer"
                          >
                            <option value="">Select carrier...</option>
                            <option value="Verizon">Verizon</option>
                            <option value="AT&T">AT&T</option>
                            <option value="T-Mobile">T-Mobile</option>
                            <option value="Sprint">Sprint</option>
                            <option value="Metro PCS">Metro PCS</option>
                            <option value="Cricket">Cricket</option>
                            <option value="Boost Mobile">Boost Mobile</option>
                            <option value="US Cellular">US Cellular</option>
                            <option value="Straight Talk">Straight Talk</option>
                            <option value="Google Fi">Google Fi</option>
                            <option value="Mint Mobile">Mint Mobile</option>
                            <option value="Visible">Visible</option>
                            <option value="Other">Other</option>
                          </select>
                          {formData.contact.phone_carrier === "Other" && (
                            <Input
                              id="phone_carrier_other"
                              data-testid="input-phone-carrier-other"
                              value={formData.contact.phone_carrier_other}
                              onChange={(e) => updateContact('phone_carrier_other', e.target.value)}
                              className="mt-2 h-12"
                              placeholder="Enter your carrier name..."
                            />
                          )}
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="email" className="text-navy font-medium">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          data-testid="input-email"
                          value={formData.contact.email}
                          onChange={(e) => updateContact('email', e.target.value)}
                          className="mt-2 h-12"
                          placeholder="john@example.com"
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="address" className="text-navy font-medium">Street Address *</Label>
                        <Input
                          id="address"
                          data-testid="input-address"
                          value={formData.contact.address}
                          onChange={(e) => updateContact('address', e.target.value)}
                          className="mt-2 h-12"
                          placeholder="123 Main Street"
                          required
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                          <Label htmlFor="city" className="text-navy font-medium">City *</Label>
                          <Input
                            id="city"
                            data-testid="input-city"
                            value={formData.contact.city}
                            onChange={(e) => updateContact('city', e.target.value)}
                            className="mt-2 h-12"
                            placeholder="Miami"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="state" className="text-navy font-medium">State *</Label>
                          <Input
                            id="state"
                            list="state-list"
                            data-testid="input-state"
                            value={formData.contact.state}
                            onChange={(e) => updateContact('state', e.target.value)}
                            className="mt-2 h-12"
                            placeholder="FL"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="zip" className="text-navy font-medium">ZIP Code *</Label>
                          <Input
                            id="zip"
                            data-testid="input-zip"
                            value={formData.contact.zip_code}
                            onChange={(e) => updateContact('zip_code', e.target.value)}
                            className="mt-2 h-12"
                            placeholder="33101"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="referral_name" className="text-navy font-medium">Referral Name</Label>
                          <Input
                            id="referral_name"
                            data-testid="input-referral-name"
                            value={formData.contact.referral_name}
                            onChange={(e) => updateContact('referral_name', e.target.value)}
                            className="mt-2 h-12"
                            placeholder="Who referred you?"
                          />
                        </div>
                      </div>

                      {/* Suggestion lists for city, state, and phone carrier */}
                      <datalist id="phone-carriers">
                        <option value="AT&amp;T"></option>
                        <option value="Verizon"></option>
                        <option value="T-Mobile"></option>
                        <option value="Boost Mobile"></option>
                        <option value="Cricket Wireless"></option>
                        <option value="Metro by T-Mobile"></option>
                        <option value="Spectrum Mobile"></option>
                      </datalist>
                      <datalist id="state-list">
                        <option value="AL"></option>
                        <option value="AK"></option>
                        <option value="AZ"></option>
                        <option value="AR"></option>
                        <option value="CA"></option>
                        <option value="CO"></option>
                        <option value="CT"></option>
                        <option value="DE"></option>
                        <option value="FL"></option>
                        <option value="GA"></option>
                        <option value="HI"></option>
                        <option value="ID"></option>
                        <option value="IL"></option>
                        <option value="IN"></option>
                        <option value="IA"></option>
                        <option value="KS"></option>
                        <option value="KY"></option>
                        <option value="LA"></option>
                        <option value="ME"></option>
                        <option value="MD"></option>
                        <option value="MA"></option>
                        <option value="MI"></option>
                        <option value="MN"></option>
                        <option value="MS"></option>
                        <option value="MO"></option>
                        <option value="MT"></option>
                        <option value="NE"></option>
                        <option value="NV"></option>
                        <option value="NH"></option>
                        <option value="NJ"></option>
                        <option value="NM"></option>
                        <option value="NY"></option>
                        <option value="NC"></option>
                        <option value="ND"></option>
                        <option value="OH"></option>
                        <option value="OK"></option>
                        <option value="OR"></option>
                        <option value="PA"></option>
                        <option value="RI"></option>
                        <option value="SC"></option>
                        <option value="SD"></option>
                        <option value="TN"></option>
                        <option value="TX"></option>
                        <option value="UT"></option>
                        <option value="VT"></option>
                        <option value="VA"></option>
                        <option value="WA"></option>
                        <option value="WV"></option>
                        <option value="WI"></option>
                        <option value="WY"></option>
                      </datalist>
                      {/* City suggestions removed as requested */}
                    </div>
                  )}

                  {/* Step 2: Tax Details */}
                  {currentStep === 2 && (
                    <div className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="filing_status" className="text-navy font-medium">Filing Status *</Label>
                          <Select
                            value={formData.tax_details.filing_status}
                            onValueChange={(value) => updateTaxDetails('filing_status', value)}
                          >
                            <SelectTrigger className="mt-2 h-12" data-testid="select-filing-status">
                              <SelectValue placeholder="Select filing status" />
                            </SelectTrigger>
                            <SelectContent>
                              {filingStatuses.map(status => (
                                <SelectItem key={status.value} value={status.value}>
                                  {status.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="occupation" className="text-navy font-medium">Occupation</Label>
                          <Input
                            id="occupation"
                            data-testid="input-occupation"
                            value={formData.tax_details.occupation}
                            onChange={(e) => updateTaxDetails('occupation', e.target.value)}
                            className="mt-2 h-12"
                            placeholder="Software Engineer"
                          />
                        </div>
                      </div>

                      {/* Spouse Information - Shows only for Married Filing Jointly */}
                      {formData.tax_details.filing_status === 'married_filing_jointly' && (
                        <div className="p-4 border border-gold/30 bg-gold/5 rounded-lg space-y-4">
                          <h3 className="font-medium text-navy flex items-center gap-2">
                            <User size={18} className="text-gold" />
                            Spouse Information
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="spouse_first_name" className="text-navy font-medium">Spouse First Name *</Label>
                              <Input
                                id="spouse_first_name"
                                data-testid="input-spouse-first-name"
                                value={formData.tax_details.spouse_first_name}
                                onChange={(e) => updateTaxDetails('spouse_first_name', e.target.value)}
                                className="mt-2 h-12"
                                placeholder="Jane"
                              />
                            </div>
                            <div>
                              <Label htmlFor="spouse_last_name" className="text-navy font-medium">Spouse Last Name *</Label>
                              <Input
                                id="spouse_last_name"
                                data-testid="input-spouse-last-name"
                                value={formData.tax_details.spouse_last_name}
                                onChange={(e) => updateTaxDetails('spouse_last_name', e.target.value)}
                                className="mt-2 h-12"
                                placeholder="Doe"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="spouse_ssn" className="text-navy font-medium">Spouse SSN *</Label>
                              <Input
                                id="spouse_ssn"
                                data-testid="input-spouse-ssn"
                                value={formData.tax_details.spouse_ssn}
                                onChange={(e) => updateTaxDetails('spouse_ssn', e.target.value)}
                                className="mt-2 h-12"
                                placeholder="XXX-XX-XXXX"
                              />
                            </div>
                            <div>
                              <Label htmlFor="spouse_dob" className="text-navy font-medium">Spouse Date of Birth *</Label>
                              <Input
                                id="spouse_dob"
                                type="text"
                                inputMode="numeric"
                                data-testid="input-spouse-dob"
                                value={formData.tax_details.spouse_dob}
                                onChange={(e) => updateTaxDetails('spouse_dob', formatDateInput(e.target.value))}
                                className="mt-2 h-12"
                                placeholder="MM/DD/YYYY"
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="spouse_occupation" className="text-navy font-medium">Spouse Occupation</Label>
                              <Input
                                id="spouse_occupation"
                                data-testid="input-spouse-occupation"
                                value={formData.tax_details.spouse_occupation}
                                onChange={(e) => updateTaxDetails('spouse_occupation', e.target.value)}
                                className="mt-2 h-12"
                                placeholder="Nurse"
                              />
                            </div>
                            <div>
                              <Label htmlFor="spouse_status_type" className="text-navy font-medium">Spouse Status Type</Label>
                              <select
                                id="spouse_status_type"
                                data-testid="input-spouse-status-type"
                                value={formData.tax_details.spouse_status_type}
                                onChange={(e) => updateTaxDetails('spouse_status_type', e.target.value)}
                                className="mt-2 h-12 w-full rounded-md border border-slate-200 bg-white px-3 text-base"
                              >
                                <option value="">Select status type</option>
                                <option value="us_citizen_born">US Citizen (Born in US)</option>
                                <option value="us_citizen_naturalized">US Citizen (Naturalized)</option>
                                <option value="resident_alien">Resident Alien</option>
                                <option value="non_resident_alien">Non-Resident Alien</option>
                                <option value="other">Other</option>
                              </select>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="spouse_phone" className="text-navy font-medium">Spouse Phone Number</Label>
                              <Input
                                id="spouse_phone"
                                data-testid="input-spouse-phone"
                                value={formData.tax_details.spouse_phone}
                                onChange={(e) => updateTaxDetails('spouse_phone', e.target.value)}
                                className="mt-2 h-12"
                                placeholder="(555) 123-4567"
                              />
                            </div>
                            <div>
                              <Label htmlFor="spouse_phone_carrier" className="text-navy font-medium">Spouse Phone Carrier</Label>
                              <select
                                id="spouse_phone_carrier"
                                data-testid="input-spouse-phone-carrier"
                                value={formData.tax_details.spouse_phone_carrier}
                                onChange={(e) => updateTaxDetails('spouse_phone_carrier', e.target.value)}
                                className="mt-2 h-12 w-full rounded-md border border-slate-200 bg-white px-3 text-base"
                              >
                                <option value="">Select carrier</option>
                                <option value="Verizon">Verizon</option>
                                <option value="AT&T">AT&T</option>
                                <option value="T-Mobile">T-Mobile</option>
                                <option value="Sprint">Sprint</option>
                                <option value="Metro PCS">Metro PCS</option>
                                <option value="Cricket">Cricket</option>
                                <option value="Boost Mobile">Boost Mobile</option>
                                <option value="US Cellular">US Cellular</option>
                                <option value="Straight Talk">Straight Talk</option>
                                <option value="Google Fi">Google Fi</option>
                                <option value="Mint Mobile">Mint Mobile</option>
                                <option value="Visible">Visible</option>
                                <option value="Other">Other</option>
                              </select>
                              {formData.tax_details.spouse_phone_carrier === "Other" && (
                                <Input
                                  id="spouse_phone_carrier_other"
                                  data-testid="input-spouse-phone-carrier-other"
                                  value={formData.tax_details.spouse_phone_carrier_other}
                                  onChange={(e) => updateTaxDetails('spouse_phone_carrier_other', e.target.value)}
                                  className="mt-2 h-12"
                                  placeholder="Enter spouse's carrier name..."
                                />
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="spouse_email" className="text-navy font-medium">Spouse Email</Label>
                              <Input
                                id="spouse_email"
                                type="email"
                                data-testid="input-spouse-email"
                                value={formData.tax_details.spouse_email}
                                onChange={(e) => updateTaxDetails('spouse_email', e.target.value)}
                                className="mt-2 h-12"
                                placeholder="spouse@email.com"
                              />
                            </div>
                            <div>
                              <Label htmlFor="spouse_gender" className="text-navy font-medium">Spouse Gender</Label>
                              <select
                                id="spouse_gender"
                                data-testid="input-spouse-gender"
                                value={formData.tax_details.spouse_gender}
                                onChange={(e) => updateTaxDetails('spouse_gender', e.target.value)}
                                className="mt-2 h-12 w-full rounded-md border border-slate-200 bg-white px-3 text-base"
                              >
                                <option value="">Select gender</option>
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                              </select>
                            </div>
                          </div>
                        </div>
                      )}

                      <div>
                        <Label className="text-navy font-medium mb-4 block">What financial services are you most interested in?</Label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {serviceTypes.map(service => (
                            <div 
                              key={service.id} 
                              className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 hover:border-gold/50 transition-colors duration-200"
                            >
                              <Checkbox
                                id={service.id}
                                data-testid={`checkbox-${service.id}`}
                                checked={formData.tax_details.services_needed.includes(service.id)}
                                onCheckedChange={() => toggleService(service.id)}
                                className="border-slate-300 data-[state=checked]:bg-navy data-[state=checked]:border-navy"
                              />
                              <Label htmlFor={service.id} className="cursor-pointer text-slate-700">
                                {service.label}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Income Type Section */}
                      <div>
                        <Label className="text-navy font-medium mb-4 block">Income Type *</Label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {incomeTypes.map(income => (
                            <div 
                              key={income.id} 
                              className="flex items-center space-x-3 p-3 rounded-lg border border-slate-200 hover:border-gold/50 transition-colors duration-200"
                            >
                              <Checkbox
                                id={`income-${income.id}`}
                                data-testid={`checkbox-income-${income.id}`}
                                checked={formData.tax_details.income_type.includes(income.id)}
                                onCheckedChange={() => toggleIncomeType(income.id)}
                                className="border-slate-300 data-[state=checked]:bg-navy data-[state=checked]:border-navy"
                              />
                              <Label htmlFor={`income-${income.id}`} className="cursor-pointer text-slate-700">
                                {income.label}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Dependents Section */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <Label className="text-navy font-medium">Please List All Dependents</Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={addDependent}
                            className="text-navy border-navy hover:bg-navy hover:text-white"
                          >
                            <Plus size={16} className="mr-1" /> Add Dependent
                          </Button>
                        </div>
                        
                        {formData.tax_details.dependents.length === 0 ? (
                          <p className="text-slate-500 text-sm italic">No dependents added. Click &quot;Add Dependent&quot; to add.</p>
                        ) : (
                          <div className="space-y-4">
                            {formData.tax_details.dependents.map((dep, index) => (
                              <div key={index} className="p-4 border border-slate-200 rounded-lg bg-slate-50">
                                <div className="flex justify-between items-center mb-3">
                                  <span className="font-medium text-navy">Dependent {index + 1}</span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeDependent(index)}
                                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  >
                                    <Trash2 size={16} />
                                  </Button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
                                  <div>
                                    <Label className="text-sm">First Name</Label>
                                    <Input
                                      value={dep.first_name || ""}
                                      onChange={(e) => updateDependent(index, 'first_name', e.target.value)}
                                      className="mt-1"
                                      placeholder="First Name"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-sm">Middle Name</Label>
                                    <Input
                                      value={dep.middle_name || ""}
                                      onChange={(e) => updateDependent(index, 'middle_name', e.target.value)}
                                      className="mt-1"
                                      placeholder="Middle Name"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-sm">Last Name</Label>
                                    <Input
                                      value={dep.last_name || ""}
                                      onChange={(e) => updateDependent(index, 'last_name', e.target.value)}
                                      className="mt-1"
                                      placeholder="Last Name"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-sm">Relationship</Label>
                                    <Select
                                      value={dep.relationship || ""}
                                      onValueChange={(value) => updateDependent(index, 'relationship', value)}
                                    >
                                      <SelectTrigger className="mt-1" data-testid={`dep-relationship-${index}`}>
                                        <SelectValue placeholder="Select" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="Son">Son</SelectItem>
                                        <SelectItem value="Daughter">Daughter</SelectItem>
                                        <SelectItem value="Stepson">Stepson</SelectItem>
                                        <SelectItem value="Stepdaughter">Stepdaughter</SelectItem>
                                        <SelectItem value="Foster child">Foster child</SelectItem>
                                        <SelectItem value="Sibling">Sibling</SelectItem>
                                        <SelectItem value="Parent">Parent</SelectItem>
                                        <SelectItem value="Other qualifying relative">Other qualifying relative</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label className="text-sm">Gender</Label>
                                    <Select
                                      value={dep.gender || ""}
                                      onValueChange={(value) => updateDependent(index, 'gender', value)}
                                    >
                                      <SelectTrigger className="mt-1" data-testid={`dep-gender-${index}`}>
                                        <SelectValue placeholder="Select" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="Male">Male</SelectItem>
                                        <SelectItem value="Female">Female</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </div>
                                  <div>
                                    <Label className="text-sm">Date of Birth</Label>
                                    <Input
                                      type="text"
                                      inputMode="numeric"
                                      value={dep.dob || ""}
                                      onChange={(e) => updateDependent(index, 'dob', formatDateInput(e.target.value))}
                                      className="mt-1"
                                      placeholder="MM/DD/YYYY"
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-sm">SSN</Label>
                                    <Input
                                      value={dep.ssn || ""}
                                      onChange={(e) => updateDependent(index, 'ssn', e.target.value)}
                                      className="mt-1"
                                      placeholder="XXX-XX-XXXX"
                                    />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Step 3: Direct Deposit */}
                  {currentStep === 3 && (
                    <div className="space-y-6">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                        <p className="text-blue-800 text-sm">
                          <strong>Direct Deposit Information</strong> - Provide your bank details for faster refund processing.
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="bank_name" className="text-navy font-medium">Bank Name *</Label>
                        <Input
                          id="bank_name"
                          data-testid="input-bank-name"
                          value={formData.direct_deposit.bank_name}
                          onChange={(e) => updateDirectDeposit('bank_name', e.target.value)}
                          className="mt-2 h-12"
                          placeholder="Chase Bank"
                          required
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="routing_number" className="text-navy font-medium">Routing Number *</Label>
                          <Input
                            id="routing_number"
                            data-testid="input-routing"
                            value={formData.direct_deposit.routing_number}
                            onChange={(e) => updateDirectDeposit('routing_number', e.target.value)}
                            className="mt-2 h-12"
                            placeholder="9 digits"
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="account_number" className="text-navy font-medium">Account Number *</Label>
                          <Input
                            id="account_number"
                            data-testid="input-account"
                            value={formData.direct_deposit.account_number}
                            onChange={(e) => updateDirectDeposit('account_number', e.target.value)}
                            className="mt-2 h-12"
                            placeholder="Account number"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="account_type" className="text-navy font-medium">Account Type *</Label>
                        <Select
                          value={formData.direct_deposit.account_type}
                          onValueChange={(value) => updateDirectDeposit('account_type', value)}
                        >
                          <SelectTrigger className="mt-2 h-12" data-testid="select-account-type">
                            <SelectValue placeholder="Select account type" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="checking">Checking</SelectItem>
                            <SelectItem value="savings">Savings</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* Step 4: Documents */}
                  {currentStep === 4 && (
                    <div className="space-y-6">
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
                        <p className="text-amber-800 text-sm">
                          <strong>Required Documents</strong> - Please upload clear copies of the following documents.
                        </p>
                      </div>


                      <div className="text-xs text-slate-500 -mt-1">
                        Tip: Use the + button to add as many documents as you need.
                      </div>

                      <DocumentUploadGroup
                        title="Copy of State Issued ID or Passport"
                        required
                        fileType="state_id"
                        error={documentErrors.state_id}
                        uploadedFiles={uploadedFiles}
                        uploadProgress={uploadProgress}
                        addDocumentSlot={addDocumentSlot}
                        removeDocumentSlot={removeDocumentSlot}
                        handleMultiFileUpload={handleMultiFileUpload}
                      />

                      <DocumentUploadGroup
                        title="Copy of Social Security Card"
                        required
                        fileType="ssn_card"
                        error={documentErrors.ssn_card}
                        uploadedFiles={uploadedFiles}
                        uploadProgress={uploadProgress}
                        addDocumentSlot={addDocumentSlot}
                        removeDocumentSlot={removeDocumentSlot}
                        handleMultiFileUpload={handleMultiFileUpload}
                      />



                              {uploadProgress.dependent_ssn !== undefined && (
                                <div className="mt-2 w-full px-6">
                                  <div className="h-2 w-full rounded bg-slate-200 overflow-hidden">
                                    <div className="h-2 bg-navy" style={{ width: `${uploadProgress.dependent_ssn}%` }} />
                                  </div>
                                  <p className="text-xs text-slate-500 mt-1">Uploading… {uploadProgress.dependent_ssn}%</p>
                                </div>
                              )}

                      <DocumentUploadGroup
                        title="Dependent Social Security Card (if applicable)"
                        required={false}
                        fileType="dependent_ssn"
                        error={false}
                        uploadedFiles={uploadedFiles}
                        uploadProgress={uploadProgress}
                        addDocumentSlot={addDocumentSlot}
                        removeDocumentSlot={removeDocumentSlot}
                        handleMultiFileUpload={handleMultiFileUpload}
                      />

                      <DocumentUploadGroup
                        title="Dependent Birth Certificate (if applicable)"
                        required={false}
                        fileType="dependent_birth_cert"
                        error={false}
                        uploadedFiles={uploadedFiles}
                        uploadProgress={uploadProgress}
                        addDocumentSlot={addDocumentSlot}
                        removeDocumentSlot={removeDocumentSlot}
                        handleMultiFileUpload={handleMultiFileUpload}
                      />

                      <DocumentUploadGroup
                        title="W-2 Form, 1099, or any wages form"
                        required
                        fileType="w2_1099"
                        error={documentErrors.w2_1099}
                        uploadedFiles={uploadedFiles}
                        uploadProgress={uploadProgress}
                        addDocumentSlot={addDocumentSlot}
                        removeDocumentSlot={removeDocumentSlot}
                        handleMultiFileUpload={handleMultiFileUpload}
                      />

                      <DocumentUploadGroup
                        title="Receipts or evidence for deductions (if applicable)"
                        required={false}
                        fileType="deduction_receipts"
                        error={false}
                        uploadedFiles={uploadedFiles}
                        uploadProgress={uploadProgress}
                        addDocumentSlot={addDocumentSlot}
                        removeDocumentSlot={removeDocumentSlot}
                        handleMultiFileUpload={handleMultiFileUpload}
                      />


                      <DocumentUploadGroup
                        title="School Form (1098T) or Class Schedule (if applicable)"
                        required={false}
                        fileType="school_form"
                        error={false}
                        uploadedFiles={uploadedFiles}
                        uploadProgress={uploadProgress}
                        addDocumentSlot={addDocumentSlot}
                        removeDocumentSlot={removeDocumentSlot}
                        handleMultiFileUpload={handleMultiFileUpload}
                      />

                      <DocumentUploadGroup
                        title="Health Insurance Form (1095A) (if applicable)"
                        required={false}
                        fileType="health_insurance"
                        error={false}
                        uploadedFiles={uploadedFiles}
                        uploadProgress={uploadProgress}
                        addDocumentSlot={addDocumentSlot}
                        removeDocumentSlot={removeDocumentSlot}
                        handleMultiFileUpload={handleMultiFileUpload}
                      />

                      {/* Other Documents - Custom named uploads */}
                      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <div className="p-6 border-b bg-slate-50">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <h3 className="font-heading text-navy text-lg">Other Documents</h3>
                              <p className="text-sm text-slate-600 mt-1">Add any additional documents (required to name each one).</p>
                            </div>
                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => setOtherDocs((prev) => [...prev, { name: "", file: null, fileId: null, progress: null }])}
                              className="h-9"
                              data-testid="add-other-doc"
                            >
                              +
                            </Button>
                          </div>
                        </div>

                        <div className="p-6 space-y-4">
                          {otherDocs.map((doc, index) => (
                            <div key={index} className="rounded-lg border border-slate-200 p-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                                <div className="md:col-span-2">
                                  <Label className="text-navy font-medium">Document Name *</Label>
                                  <Input
                                    value={doc.name}
                                    onChange={(e) =>
                                      setOtherDocs((prev) =>
                                        prev.map((d, i) => (i === index ? { ...d, name: e.target.value } : d))
                                      )
                                    }
                                    className="mt-2 h-10"
                                    placeholder="Enter document name"
                                    data-testid={`other-doc-name-${index}`}
                                  />
                                </div>

                                <div>
                                  <label className="inline-flex items-center justify-center w-full">
                                    <span className="w-full text-center rounded-md bg-navy text-white px-3 py-2 text-sm cursor-pointer hover:bg-navy-light">
                                      Upload/Take Pictures
                                    </span>
                                    <input
                                      type="file"
                                      className="hidden"
                                      accept="image/*,.pdf"
                                      onChange={(e) => handleOtherDocUpload(index, e)}
                                    />
                                  </label>
                                </div>
                              </div>

                              {doc.progress !== null && (
                                <div className="mt-3">
                                  <div className="h-2 w-full rounded bg-slate-200 overflow-hidden">
                                    <div className="h-2 bg-navy" style={{ width: `${doc.progress}%` }} />
                                  </div>
                                  <p className="text-xs text-slate-500 mt-1">Uploading… {doc.progress}%</p>
                                </div>
                              )}

                              {doc.file && (
                                <p className="text-sm text-green-800 mt-3 break-all">{doc.file.name}</p>
                              )}

                              {otherDocs.length > 1 && (
                                <div className="mt-3 flex justify-end">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => setOtherDocs((prev) => prev.filter((_, i) => i !== index))}
                                    className="text-red-600 hover:text-red-700"
                                    data-testid={`remove-other-doc-${index}`}
                                  >
                                    Remove
                                  </Button>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 5: Eligibility Questions */}
                  {currentStep === 5 && (
                    <div className="space-y-6">
                      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                        <p className="text-purple-800 text-sm font-medium">
                          PLEASE ANSWER EACH QUESTION CAREFULLY TO DETERMINE CREDITS ELIGIBILITY. ANSWERING &quot;YES&quot; DOES NOT GUARANTEE ELIGIBILITY. ALL CREDITS ARE SUBJECT TO IRS RULES AND VERIFICATION.
                        </p>
                      </div>

                      {/* Marital Status as of Dec 31 */}
                      <div>
                        <Label className="text-navy font-medium">As of December 31, of the tax year, were you... *</Label>
                        <div className="mt-3 space-y-2">
                          {maritalStatuses.map(status => (
                            <div key={status.value} className="flex items-center space-x-3">
                              <Checkbox
                                id={`marital-${status.value}`}
                                checked={formData.eligibility.marital_status_dec31 === status.value}
                                onCheckedChange={(checked) => {
                                  if (checked) updateEligibility('marital_status_dec31', status.value);
                                }}
                                className="border-slate-300 data-[state=checked]:bg-navy"
                              />
                              <Label htmlFor={`marital-${status.value}`} className="cursor-pointer">{status.label}</Label>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Dependent lived with you */}
                      <div>
                        <Label className="text-navy font-medium">Did a qualifying person (such as a CHILD or DEPENDENT) live with you for more than half the year (6 months)? *</Label>
                        <div className="mt-3 flex gap-6">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="dependent-yes"
                              checked={formData.eligibility.dependent_lived_with_you === true}
                              onCheckedChange={(checked) => { if (checked) updateEligibility('dependent_lived_with_you', true); }}
                              className="border-slate-300 data-[state=checked]:bg-navy"
                            />
                            <Label htmlFor="dependent-yes" className="cursor-pointer">Yes</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="dependent-no"
                              checked={formData.eligibility.dependent_lived_with_you === false}


                              onCheckedChange={(checked) => { if (checked) updateEligibility('dependent_lived_with_you', false); }}
                              className="border-slate-300 data-[state=checked]:bg-navy"
                            />
                            <Label htmlFor="dependent-no" className="cursor-pointer">No</Label>
                          </div>
                        </div>
                      </div>

                      {/* Paid more than half expenses */}
                      <div>
                        <Label className="text-navy font-medium">Did you pay MORE THAN HALF of the household expenses, such as rent, utilities, food, and other living costs? *</Label>
                        <div className="mt-3 flex gap-6">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="expenses-yes"
                              checked={formData.eligibility.paid_more_than_half_expenses === true}
                              onCheckedChange={(checked) => { if (checked) updateEligibility('paid_more_than_half_expenses', true); }}
                              className="border-slate-300 data-[state=checked]:bg-navy"
                            />
                            <Label htmlFor="expenses-yes" className="cursor-pointer">Yes</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="expenses-no"
                              checked={formData.eligibility.paid_more_than_half_expenses === false}
                              onCheckedChange={(checked) => { if (checked) updateEligibility('paid_more_than_half_expenses', false); }}
                              className="border-slate-300 data-[state=checked]:bg-navy"
                            />
                            <Label htmlFor="expenses-no" className="cursor-pointer">No</Label>
                          </div>
                        </div>
                      </div>

                      {/* Dependent is qualifying relative */}

                      <div>
                        <Label className="text-navy font-medium">Is the Dependent your child, stepchild, foster child, sibling, parent, or other qualifying relative under IRS rules? *</Label>
                        <div className="mt-3 flex gap-6">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="qualifying-yes"
                              checked={formData.eligibility.dependent_is_qualifying_relative === true}
                              onCheckedChange={(checked) => { if (checked) updateEligibility('dependent_is_qualifying_relative', true); }}
                              className="border-slate-300 data-[state=checked]:bg-navy"
                            />
                            <Label htmlFor="qualifying-yes" className="cursor-pointer">Yes</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="qualifying-no"
                              checked={formData.eligibility.dependent_is_qualifying_relative === false}
                              onCheckedChange={(checked) => { if (checked) updateEligibility('dependent_is_qualifying_relative', false); }}
                              className="border-slate-300 data-[state=checked]:bg-navy"
                            />
                            <Label htmlFor="qualifying-no" className="cursor-pointer">No</Label>
                          </div>
                        </div>
                      </div>

                      {/* Anyone else can claim */}
                      <div>
                        <Label className="text-navy font-medium">Is anyone else allowed to claim this dependent on their tax return? *</Label>
                        <div className="mt-3 flex gap-6">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="otherclaim-yes"
                              checked={formData.eligibility.anyone_else_can_claim_dependent === true}
                              onCheckedChange={(checked) => { if (checked) updateEligibility('anyone_else_can_claim_dependent', true); }}
                              className="border-slate-300 data-[state=checked]:bg-navy"
                            />
                            <Label htmlFor="otherclaim-yes" className="cursor-pointer">Yes</Label>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="otherclaim-no"
                              checked={formData.eligibility.anyone_else_can_claim_dependent === false}
                              onCheckedChange={(checked) => { if (checked) updateEligibility('anyone_else_can_claim_dependent', false); }}
                              className="border-slate-300 data-[state=checked]:bg-navy"
                            />
                            <Label htmlFor="otherclaim-no" className="cursor-pointer">No</Label>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Step 6: Review & Submit */}
                  {currentStep === 6 && (
                    <div className="space-y-6">
                      <div className="flex justify-between items-center mb-3">
                        <div>
                          <h2 className="font-heading text-xl text-navy">Review & Submit</h2>
                          <p className="text-slate-600">Please review your information carefully before submitting.</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-slate-500">Step 6 of 6</p>
                        </div>
                      </div>

                      {/* Optional Appointment Scheduling */}
                      <div className="mb-6 border border-slate-200 rounded-lg p-4 bg-slate-50/60">
                        <h3 className="font-semibold text-navy mb-2">Optional: Schedule a 30-minute call</h3>
                        <p className="text-sm text-slate-600 mb-3">
                          You can schedule a 30-minute call with WMJ Enterprise Services between 10:00 AM and 6:00 PM (EST). This step is optional.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label className="text-navy font-medium text-sm">Preferred Date</Label>
                            <Popover open={appointmentPopoverOpen} onOpenChange={setAppointmentPopoverOpen}>
                              <PopoverTrigger asChild>
                                <Button
                                  variant="outline"
                                  className="mt-1 h-10 w-full justify-start text-left font-normal"
                                  data-testid="appointment-date-picker"
                                >
                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                  {appointmentDate ? appointmentDate : "Select a date"}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                  mode="single"
                                  selected={appointmentDate ? new Date(mmddyyyyToIso(appointmentDate)) : undefined}
                                  onSelect={(date) => {
                                    if (!date) return;
                                    setAppointmentDate(format(date, "MM/dd/yyyy"));
                                    setAppointmentPopoverOpen(false);
                                  }}
                                  initialFocus
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                          <div>
                            <Label htmlFor="appointment_time" className="text-navy font-medium text-sm">Preferred Time (EST)</Label>
                            <select
                              id="appointment_time"
                              value={appointmentTime}
                              onChange={(e) => setAppointmentTime(e.target.value)}
                              className="mt-1 h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-navy"
                            >
                              <option value="">No preference / Skip</option>
                              {appointmentSlots.map((slot) => (
                                <option key={slot} value={slot}>{slot}</option>
                              ))}
                        <p className="text-xs text-slate-500 mt-2">
                          After scheduling, you can reply to the confirmation email or call us to reschedule.
                        </p>

                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Additional Notes */}
                      <div>
                        <Label htmlFor="notes" className="text-navy font-medium">Any notes for your Tax Preparer?</Label>
                        <Textarea
                          id="notes"
                          value={formData.documents.additional_notes}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            documents: { ...prev.documents, additional_notes: e.target.value }
                          }))}
                          className="mt-2 min-h-[100px]"
                          placeholder="Any additional information..."
                        />
                      </div>

                      {/* Confirmations - Simplified */}
                      <div className="space-y-4 border-t pt-6">
                        <h3 className="font-medium text-navy">Please confirm the following:</h3>
                        
                        <div className="flex items-start space-x-3 p-3 bg-slate-50 rounded-lg">
                          <Checkbox
                            id="intake_consent"
                            checked={intakeConsent}
                            onCheckedChange={(checked) => setIntakeConsent(Boolean(checked))}
                            className="mt-1 border-slate-300 data-[state=checked]:bg-navy"
                            required
                          />
                          <Label htmlFor="intake_consent" className="cursor-pointer text-sm leading-relaxed">
                            I consent to submit my intake information and confirm that the details provided are true and accurate. *
                          </Label>
                        </div>
                      </div>

                      {/* Signature */}
                      <div>
                        <Label htmlFor="signature" className="text-navy font-medium">Signature (Type your full name) *</Label>
                        <Input
                          id="signature"
                          value={formData.signature}
                          onChange={(e) => setFormData(prev => ({ ...prev, signature: e.target.value }))}
                          className="mt-2 h-12"
                          placeholder="John Doe"
                          required
                        />
                      </div>

                      {/* SMS Consent */}
                      <div className="flex items-start space-x-3 p-4 border border-slate-200 rounded-lg">
                        <Checkbox
                          id="sms_consent"
                          checked={formData.confirmations.consent_sms}
                          onCheckedChange={(checked) => updateConfirmations('consent_sms', checked)}
                          className="mt-1 border-slate-300 data-[state=checked]:bg-navy"
                        />
                        <Label htmlFor="sms_consent" className="cursor-pointer text-sm leading-relaxed text-slate-600">
                          By checking this box, you agree to receive SMS messages from WMJ Enterprise Services LLC at 901-401-3054 regarding your tax preparation, status updates, and related services. Message and data rates may apply.
                        </Label>


                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
                {currentStep > 1 ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    className="border-slate-300 text-slate-700 hover:bg-slate-50"
                    data-testid="btn-prev"
                  >
                    <ArrowLeft className="mr-2" size={18} />
                    Previous
                  </Button>
                ) : (
                  <div></div>
                )}

                {currentStep < steps.length ? (
                  <Button
                    type="button"
                    onClick={nextStep}
                    className="bg-navy text-white hover:bg-navy-dark"
                    data-testid="btn-next"
                  >
                    Next
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="bg-gold text-navy hover:bg-gold-hover font-semibold px-8"
                    data-testid="btn-submit"
                  >
                    {isSubmitting ? "Submitting..." : "Submit Form"}
                    <CheckCircle className="ml-2" size={18} />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Privacy Links */}
          <div className="text-center mt-6">
            <a href="/privacy" className="text-navy hover:text-gold text-sm">Privacy Policy</a>
            <span className="mx-2 text-slate-400">|</span>
            <a href="/terms" className="text-navy hover:text-gold text-sm">Terms of Service</a>
          </div>
        </div>
      </main>

      {/* Portal Signup Prompt Modal */}
      <Dialog open={showPortalSignupPrompt} onOpenChange={setShowPortalSignupPrompt}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading text-navy flex items-center gap-2">
              <CheckCircle className="text-green-500" size={28} />
              Intake Submitted!
            </DialogTitle>
            <DialogDescription className="text-slate-600 pt-2">
              Your intake form has been successfully submitted. Our team will review your information and contact you shortly.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-gold/10 border border-gold/30 rounded-lg p-4 mt-4">
            <h4 className="font-semibold text-navy mb-2">🎉 Create Your Client Portal Account</h4>
            <p className="text-sm text-slate-600 mb-4">
              Stay connected with WMJ Enterprise Services! Create a free portal account to:
            </p>
            <ul className="text-sm text-slate-600 space-y-2 mb-4">
              <li className="flex items-start gap-2">
                <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                <span>Track your tax return status in real-time</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                <span>View and manage your appointments</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                <span>Upload additional documents securely</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                <span>Update your contact information anytime</span>
              </li>
            </ul>
          </div>

          <div className="flex flex-col gap-3 mt-4">
            <Button
              onClick={() => navigate("/portal")}
              className="w-full bg-navy hover:bg-navy-light text-white font-semibold py-3"
              data-testid="create-portal-btn"
            >
              Create Portal Account
            </Button>
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="w-full border-slate-300 text-slate-600"
              data-testid="skip-portal-btn"
            >
              Maybe Later
            </Button>
          </div>
          
          <p className="text-xs text-center text-slate-400 mt-2">
            You can always create an account later at wmjenterpriseservices.com/portal
          </p>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default IntakeForm;
